import { Component, OnInit, OnDestroy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppStateService } from '../../services/app-state.service';
import { TranslateService } from '../../services/translate.service';
import { PrebookContext } from '../../models/Order';
import { TimeUtils } from '../../utils/Utils';
import { BottomSheetService } from '../../services/bottom-sheet.service';
import { Router } from '@angular/router';

/**
 * Booking banner shown while the guest is in prebook context (order responses
 * carry a `prebook` block). Renders nothing otherwise.
 */
@Component({
  standalone: true,
  selector: 'app-prebook-banner',
  imports: [CommonModule],
  template: `
    <div *ngIf="prebook" class="mx-4 mb-4 bg-theme-surface2-50 rounded-xl p-4">
      <div class="font-bold text-theme-primary">
        <i class="fas fa-calendar-check mr-2"></i>{{getString("Förbeställning till bokning")}} #{{prebook.prebooking.external_ref}}
      </div>
      <div class="mt-1 text-theme-text">
        {{bookingTimeText}}
        <span *ngIf="prebook.booking.pax"> &middot; {{prebook.booking.pax}} {{getString("gäster")}}</span>
      </div>
      <div class="mt-2 text-theme-text whitespace-pre-line" *ngIf="prebook.template.guest_message">
        {{prebook.template.guest_message}}
      </div>
      <div class="mt-1 text-theme-text2" *ngIf="prebook.can_edit">
        {{lockText}}
      </div>
      @if (prebook.booked) {
        <!-- Sent: the party's order is the booking's now, the drafts are empty -->
        <div class="mt-2 text-theme-text">{{ getString("Beställningen är skickad till restaurangen.") }}</div>
        <button class="mt-3 font-bold text-theme-primary" (click)="openBooking()">
          <i class="fas fa-receipt mr-2"></i>{{ getString("Visa beställningen") }}
        </button>
      } @else {
        <div class="mt-1 text-theme-text2" *ngIf="!prebook.can_edit">
          {{getString(prebook.prebooking.status === 'cancelled' ? "Bokningen är avbokad." : "Tiden för förbeställning har passerat.")}}
        </div>
        @if (prebook.role === 'participant') {
          <div class="mt-2 text-theme-text">{{ participantText() }}</div>
        }
        @if (partyText) {
          <div class="mt-1 text-theme-text2">{{ partyText }}</div>
        }
      }
      @if (prebook.role === 'booker' && prebook.can_invite) {
        <button class="mt-3 font-bold text-theme-primary" (click)="openInvite()">
          <i class="fas fa-share-nodes mr-2"></i>{{ getString(prebook.invite_url ? "Dela länken till sällskapet" : "Låt alla beställa själva") }}
        </button>
      }
    </div>
  `
})
export class PrebookBannerComponent implements OnInit, OnDestroy {
  prebook?: PrebookContext;
  bookingTimeText = "";
  // Who of the party has joined, and who is done: "Sällskapet: Anna, Erik (klar)"
  partyText = "";
  lockText = "";
  private timer?: any;

  constructor(private appStateService: AppStateService, private translate: TranslateService,
              private destroyRef: DestroyRef, private sheetService: BottomSheetService, private router: Router) { }

  ngOnInit(): void {
    this.appStateService.observeAppState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(appState => {
        this.prebook = appState.order?.prebook;
        this.updateTexts();
      });
    this.timer = setInterval(() => this.updateTexts(), 30000);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private updateTexts() {
    if (!this.prebook) {
      return;
    }
    this.bookingTimeText = this.formatLocalTime(this.prebook.booking.time_local);
    this.partyText = this.prebook.party
      ? this.getString("Sällskapet") + ": " + this.prebook.party
        .map(guest => guest.done ? `${guest.name} (${this.getString("har valt klart")})` : guest.name).join(", ")
      : "";
    this.lockText = this.buildLockText(this.prebook.edit_lock_at);
  }

  private formatLocalTime(timeLocal?: string): string {
    if (!timeLocal) {
      return "";
    }
    // "2026-08-15T18:00" -> "2026-08-15 18:00"
    return timeLocal.substring(0, 16).replace("T", " ");
  }

  private buildLockText(editLockAtUtc: string): string {
    // Server sends naive UTC ISO; append Z so Date parses it as UTC
    const lockAt = new Date(editLockAtUtc + "Z");
    const minutesLeft = Math.floor((lockAt.getTime() - Date.now()) / 60000);
    if (minutesLeft < 60) {
      return this.getString("Beställningen stänger om") + " " + Math.max(minutesLeft, 0) + " " + this.getString("minuter");
    }
    return this.getString("Beställ senast") + " " + TimeUtils.formatDeadlineClock(editLockAtUtc);
  }

  participantText(): string {
    const booker = this.prebook?.party?.find(guest => guest.is_booker)?.name ?? this.prebook?.booking.name;
    const done = this.prebook?.party?.find(guest => guest.is_me)?.done;
    return this.getString(done ? "Du har valt klart." : "Du väljer din egen mat.") + " " +
      (booker ? `${booker} ${this.getString("skickar hela sällskapets beställning.")}` : this.getString("Den som bokade skickar hela sällskapets beställning."));
  }

  openBooking() {
    this.router.navigate([this.appStateService.venueId, "booking", this.prebook!.booked!.booking_ref]);
  }

  openInvite() {
    this.sheetService.displaySheetClass("prebook-invite",
      this.getString(this.prebook?.invite_url ? "Bjud in sällskapet" : "Hur vill ni beställa?"));
  }

  getString(s: string): string {
    return this.translate.getString(s);
  }
}
